/**
 * Bar Intel's **Visual** view: one vertical range bar per period, the close a
 * dot inside it. `docs/bar-intelligence-close-mock.html` B′, owner 2026-10-12.
 *
 * - All-time on the left, Yesterday on the right; high on top, like the price axis.
 * - Each column is its own period's range — 0% at the low, 100% at the high. One
 *   shared price scale would make Yesterday a sliver beside YTD.
 * - Distances are from the **close**, `(close − level) ÷ level`: the table's sign.
 *   **Colour follows sign** (− red, + green); amber ◆ only within Near %.
 *
 * Renders what the chart published; computes only the position and the two
 * distances, from levels already in the snapshot.
 */

import type { CSSProperties, ReactElement } from "react";
import {
  fmtPrice,
  fmtSigned,
  periodLabel,
  type BiLevel,
  type BiPeriod,
} from "../lib/bar-intelligence";

/** Long term on the left (owner). */
const ORDER: readonly BiPeriod[] = [
  "ath",
  "52w",
  "y",
  "q",
  "mtd",
  "pq",
  "m",
  "w",
  "d",
];

const TRACK_H = 150;

/** `(close − level) ÷ level` as a %, capped so an all-time low of cents stays readable. */
function distance(close: number, level: number): { text: string; v: number } {
  const v = ((close - level) / level) * 100;
  if (v > 999) return { text: ">+999%", v };
  return { text: `${fmtSigned(v, 1)}%`, v };
}

export function BarIntelRanges({
  live,
  close,
  near,
}: {
  live: ReadonlyMap<string, BiLevel>;
  close: number;
  near: number;
}): ReactElement {
  const periods = ORDER.filter(
    (p) => live.has(`${p}:high`) && live.has(`${p}:low`),
  );
  return (
    <div style={WRAP}>
      {periods.map((p) => {
        const hi = live.get(`${p}:high`)!;
        const lo = live.get(`${p}:low`)!;
        const width = hi.price - lo.price;
        const pos = width > 0 ? ((close - lo.price) / width) * 100 : 50;
        return (
          <div key={p} style={COL}>
            <Edge level={hi} close={close} near={near} />
            <div style={TRACK}>
              <div style={FILL} />
              {pos >= 0 && pos <= 100 && (
                <>
                  <div style={{ ...DOT, bottom: `${pos}%` }} />
                  <div style={{ ...PCT, bottom: `${pos}%` }}>
                    {Math.round(pos)}%
                  </div>
                </>
              )}
            </div>
            <div style={OUT}>
              {pos > 100 ? "▲ above" : pos < 0 ? "▼ below" : ""}
            </div>
            <Edge level={lo} close={close} near={near} />
            <div style={NAME}>{periodLabel(p)}</div>
          </div>
        );
      })}
    </div>
  );
}

function Edge({
  level,
  close,
  near,
}: {
  level: BiLevel;
  close: number;
  near: number;
}): ReactElement {
  const d = distance(close, level.price);
  const isNear = Math.abs(d.v) <= near;
  const color = isNear
    ? "var(--qf-warn)"
    : d.v < 0
      ? "var(--qf-negative)"
      : d.v > 0
        ? "var(--qf-positive)"
        : undefined;
  return (
    <div
      style={EDGE}
      title={`${level.name} set ${new Date(level.setT * 1000).toISOString().slice(0, 10)} · ${level.days}d`}
    >
      {fmtPrice(level.price)}
      <br />
      <span style={{ color }}>
        {d.text}
        {isNear ? " ◆" : ""}
      </span>
    </div>
  );
}

const WRAP: CSSProperties = {
  display: "grid",
  gridAutoFlow: "column",
  gridAutoColumns: "minmax(64px, 1fr)",
  gap: 6,
  overflowX: "auto",
  padding: "6px 0 4px",
  fontVariantNumeric: "tabular-nums",
};
const COL: CSSProperties = {
  display: "grid",
  gridTemplateRows: `34px ${TRACK_H}px 20px 34px 18px`,
  justifyItems: "center",
  textAlign: "center",
  fontSize: 12,
};
const EDGE: CSSProperties = { lineHeight: 1.3, whiteSpace: "nowrap" };
const TRACK: CSSProperties = {
  position: "relative",
  width: 14,
  height: TRACK_H,
  margin: "2px 0",
};
const FILL: CSSProperties = {
  position: "absolute",
  inset: 0,
  borderRadius: 7,
  background: "color-mix(in srgb, var(--qf-accent) 18%, transparent)",
  border: "1px solid var(--qf-border)",
};
const DOT: CSSProperties = {
  position: "absolute",
  left: -1,
  width: 16,
  height: 16,
  marginBottom: -8,
  borderRadius: "50%",
  background: "var(--qf-accent)",
  border: "2px solid var(--qf-surface)",
};
const PCT: CSSProperties = {
  position: "absolute",
  left: 20,
  marginBottom: -8,
  fontWeight: 600,
  color: "var(--qf-accent)",
  whiteSpace: "nowrap",
};
const OUT: CSSProperties = {
  color: "var(--qf-accent)",
  fontWeight: 600,
  whiteSpace: "nowrap",
  lineHeight: "20px",
};
const NAME: CSSProperties = {
  color: "var(--qf-text-secondary)",
  fontWeight: 600,
  whiteSpace: "nowrap",
};
